import React from 'react';
import { ErrorPage } from './ErrorPage';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  crashed: boolean;
}

/**
 * Catches a render crash anywhere below it and shows `ErrorPage` instead
 * of a blank screen (#652). Kept identical in front-porch and
 * member-portal — edit both, or neither.
 *
 * Without it any throw during render unmounted the whole app and left a
 * white page with the reason only in the console. Mounted once, at the
 * root; it catches render errors only, not a rejected request, which each
 * screen still shows with `ErrorAlert`.
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { crashed: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { crashed: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('Render crash caught by ErrorBoundary', error, info);
  }

  render(): React.ReactNode {
    if (!this.state.crashed) return this.props.children;
    return (
      <div className="min-h-screen bg-panel">
        <ErrorPage
          title="Something went wrong"
          message="This page hit an unexpected problem. Reloading usually fixes it; if it keeps happening, let us know what you were doing."
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }
}

export default ErrorBoundary;
